/**
 * recruitment-media-store.js
 * 媒体連携に関連するデータストア
 */
// 媒体関連APIリクエスト用I/F
import recruitmentMediaInterface from '@/api/recruitment-media/recruitment-media-interface'
// 媒体関連APIリクエスト用API
import recruitmentMediaApi from '@/api/recruitment-media/recruitment-media-api'
import fileDownload from '@/hrm-frontend/modules/functions/file-download'

export default {
  namespaced: true,
  state: {
    // モーダル状態遷移
    modalState: {
      // 媒体連携項目マスター設定モーダル
      mediaCooperationItemMaster: false,
    },
    // 媒体情報リスト
    recruitmentMediaInfoList: [],
    // 選考状況リスト
    progressList: [],
    // Seekerフィールドリスト
    seekerFieldList: [],
    // 媒体連携項目マスター一覧
    mediaCooperationItemMasterNameList: [],
    // 媒体連携項目マスター
    currentMaster: {},
    // 媒体連携項目マスターテンプレート
    mediaCooperationItemMasterTemplate: {},
    // 項目データテンプレート
    itemDataTemplate: {},
    // データ読み込み中フラグ
    isDataLoading: false,
    // 読み込み件数
    loadCount: 0
  },
  actions: {
    /**
     * モーダルオープン
     * @param {Object} param0 ストアオブジェクト
     * @param {String} modalName モーダル名
     */
    openModal ({commit}, modalName) {
      try {
        commit('openModal', modalName)
      } catch (error) {
        throw error
      }
    },
    /**
     * モーダルクローズ
     * @param {Object} param0 ストアオブジェクト
     * @param {String} modalName モーダル名
     */
    closeModal ({commit}, modalName) {
      try {
        commit('closeModal', modalName)
      } catch (error) {
        throw error
      }
    },

    // ==================================================
    // 媒体情報関連
    // ==================================================

    /**
     * 全媒体情報取得
     * @param {Object} param0 ストアオブジェクト
     */
    getAllRecruitmentMediaInfo ({commit}) {
      try {
        const recruitmentMediaIf = new recruitmentMediaInterface()
        const queryParameter = recruitmentMediaIf.makeAllRecruitmentMediaInfoParameter()
        return recruitmentMediaApi.getRecruitmentMediaInfo(queryParameter)
          .then(response => {
            // 取得した媒体情報リストをストアに設定
            commit('setRecruitmentMediaInfo', response.data)
            return
          })
          .catch(error => {
            throw error
          })
      } catch (error) {
        throw error
      }
    },
    /**
     * 選考状況リスト取得
     * @param {Object} param0 ストアオブジェクト
     */
    getProgressList ({commit}) {
      try {
        const recruitmentMediaIf = new recruitmentMediaInterface()
        const queryParameter = recruitmentMediaIf.makeGetProgressListParameter()
        return recruitmentMediaApi.getProgressList(queryParameter)
          .then(response => {
            // 取得した選考状況リストをストアに設定
            commit('setProgressList', response.data)
            return
          })
          .catch(error => {
            throw error
          })
      } catch (error) {
        throw error
      }
    },
    /**
     * Seekerフィールドリスト取得
     * @param {Object} param0 ストアオブジェクト
     */
    getSeekerFieldList ({commit}) {
      try {
        const recruitmentMediaIf = new recruitmentMediaInterface()
        const queryParameter = recruitmentMediaIf.makeGetSeekerFieldListParameter()
        return recruitmentMediaApi.getSeekerFieldList(queryParameter)
          .then(response => {
            // 取得したSeekerフィールドリストをストアに設定
            commit('setSeekerFieldList', response.data)
            return
          })
          .catch(error => {
            throw error
          })
      } catch (error) {
        throw error
      }
    },

    // ==================================================
    // 媒体連携項目マスター関連
    // ==================================================

    /**
     * 媒体連携項目マスターデータ入れ替え
     * @param {Object} param0 ストアオブジェクト
     * @param {Object} currentMaster 媒体連携項目マスターデータ
     */
    setCurrentMaster ({commit}, currentMaster) {
      commit('setCurrentMaster', currentMaster)
    },
    /**
     * 表示中媒体連携項目マスタークリア
     * @param {Object} param0 ストアオブジェクト
     */
    clearCurrentMaster ({commit}) {
      commit('clearCurrentMaster')
    },
    /**
     * 媒体連携項目マスター新規作成
     * @param {Object} param0 ストアオブジェクト
     * @param {Object} createPayload 新規作成ペイロード
     */
    async createMediaCooperationItemMaster ({commit}, itemMaster) {
      try {
        const recruitmentMediaIf = new recruitmentMediaInterface()
        const createPayload = recruitmentMediaIf.makeItemMasterCreatePayload(itemMaster)
        if (createPayload.isSuccess === false) {
          throw createPayload
        }

        const response = await recruitmentMediaApi.postMediaCooperationItemMaster(createPayload.payload)
        const createTaskStatusParameter = recruitmentMediaIf.createGetCreateItemMasterAsyncTaskParameter(response.data['id'])

        const checkStatus = async () => {
          const statusResponse = await recruitmentMediaApi.getMediaCooperationItemMaster(createTaskStatusParameter)
          if (statusResponse.data.status === 'completed') {
            // 新規作成した媒体連携項目マスターをストアに追加
            commit('createMediaCooperationItemMaster', statusResponse.data.result)
            // 媒体連携項目マスターリストの表示順を更新する
            commit('updateMediaCooperationItemMasterSort', itemMaster)
            return createPayload
          } else {
            // まだ完了していない場合は再度チェック
            return new Promise((resolve) => {
              setTimeout(() => resolve(checkStatus()), 1000)
            })
          }
        }
        return await checkStatus()
      } catch (error) {
        throw error
      }
    },
    /**
     * 媒体連携項目マスター一覧取得
     * @param {Object} param0 ストアオブジェクト
     */
    async getMediaCooperationItemMasterList ({commit}) {
      try {
        const recruitmentMediaIf = new recruitmentMediaInterface()
        const queryParameter = recruitmentMediaIf.makeGetItemMasterListParameter()
        return await recruitmentMediaApi.getMediaCooperationItemMaster(queryParameter)
          .then((response) => {
            // 媒体連携項目マスター一覧をストアに設定
            commit('setItemMasterList', response.data)
            return
          })
          .catch(error => {
            throw error
          })
      } catch (error) {
        throw error
      }
    },
    /**
     * 媒体連携項目マスターデータ取得
     * @param {Object} param0 ストアオブジェクト
     */
    async getMediaCooperationItemMaster ({commit}, data) {
      try {
        const recruitmentMediaIf = new recruitmentMediaInterface()
        const queryParameter = recruitmentMediaIf.makeGetItemMasterDataParameter(data)
        return await recruitmentMediaApi.getMediaCooperationItemMaster(queryParameter)
          .then(response => {
            // 取得した媒体連携項目マスターをストアに設定
            commit('setMediaCooperationItemMaster', response.data)
            return
          })
          .catch(error => {
            throw error
          })
      } catch (error) {
        throw error
      }
    },
    /**
     * 媒体連携項目マスターテンプレート取得
     * @param {Object} param0 ストアオブジェクト
     */
    async getMediaCooperationItemMasterTemplate ({commit}) {
      try {
        const recruitmentMediaIf = new recruitmentMediaInterface()
        const queryParameter = recruitmentMediaIf.makeItemMasterGetTemplateParameter()
        return await recruitmentMediaApi.getMediaCooperationItemMaster(queryParameter)
          .then((response) => {
            // 媒体連携項目マスターテンプレートをストアに設定
            commit('setItemMasterTemplate', response.data)
            return
          })
          .catch(error => {
            throw error
          })
      } catch (error) {
        throw error
      }
    },
    /**
     * 媒体連携項目マスターリストの表示順更新
     * @param {Object} param0 ストアオブジェクト
     * @param {Object} itemMaster 媒体連携項目マスターデータ
     */
    async updateMediaCooperationItemMasterSort ({commit}, itemMaster) {
      try {
        // 媒体連携項目マスターリストの表示順を更新する
        await commit('updateMediaCooperationItemMasterSort', itemMaster)
      } catch (error) {
        throw error
      }
    },
    /**
     * 媒体連携項目マスター名変更
     * @param {Object} param0 ストアオブジェクト
     * @param {Object} itemMaster 媒体連携項目マスターデータ
     * @return {Object} レスポンス
     */
    async saveMediaCooperationItemMasterName ({commit}, itemMasterList) {
      try {
        const recruitmentMediaIf = new recruitmentMediaInterface()
        const savePayload = recruitmentMediaIf.makeItemMasterNameSavePayload(itemMasterList)
        if (savePayload.isSuccess === false) {
          throw savePayload.payload
        }
        return await recruitmentMediaApi.patchMediaCooperationItemMaster(savePayload.payload)
          .then((response) => {
            // 更新した媒体連携項目マスター名をストアに設定
            commit('updateMediaCooperationItemMasterName', response.data)
            return response.data
          })
          .catch(error => {
            throw error
          })
      } catch (error) {
        throw error
      }
    },
    /**
     * 媒体連携項目マスター保存
     * @param {Object} param0 ストアオブジェクト
     * @param {Object} itemMaster 媒体連携項目マスター更新データ
     * @return {Object} レスポンス
     */
    async saveMediaCooperationItemMaster ({commit}, itemMaster) {
      try {
        const recruitmentMediaIf = new recruitmentMediaInterface()
        const savePayload = recruitmentMediaIf.makeItemMasterSavePayload(itemMaster)
        if (savePayload.isSuccess === false) {
          throw savePayload
        }
        return await recruitmentMediaApi.putMediaCooperationItemMaster(savePayload.payload)
          .then((response) => {
            return response
          })
          .catch(error => {
            throw error
          })
      } catch (error) {
        throw error
      }
    },
    /**
     * 媒体連携項目マスター削除
     * @param {Object} param0 ストアオブジェクト
     * @param {Object} queryParameter 削除クエリパラメーター
     */
    async deleteMediaCooperationItemMaster ({commit}, masterId) {
      try {
        const recruitmentMediaIf = new recruitmentMediaInterface()
        const queryParameter = recruitmentMediaIf.makeItemMasterDataDeleteParameter(masterId)
        return await recruitmentMediaApi.deleteMediaCooperationItemMaster(queryParameter)
          .then(() => {
            // 削除結果をストアに設定
            commit('deleteMediaCooperationItemMaster', masterId)
            return
          })
          .catch(error => {
            throw error
          })
      } catch (error) {
        throw error
      }
    },
    /**
     * CSVテンプレートダウンロード
     * @param {Object} param0 ストアオブジェクト
     */
    itemMasterCsvDownload ({commit}, {currentMaster, masterName}) {
      try {
        const recruitmentMediaIf = new recruitmentMediaInterface()
        const payload = recruitmentMediaIf.makeItemMasterCsvTemplateDownloadPayload(currentMaster, masterName)
        if (payload) {
          return recruitmentMediaApi.postRecruitmentMediaFile(payload)
            .then((res) => {
              // レスポンスをダウンロード
              fileDownload.fileDownload(res)
              return
            })
            .catch(error => {
              throw error
            })
        } else {
          return
        }
      } catch (error) {
        throw error
      }
    },
    /**
     * 媒体連携項目マスターインポート
     * @param {Object} param0 ストアオブジェクト
     * @param {Object} importFile インポートファイル
     * @return {Object} レスポンス
     */
    async importMediaCooperationItemMaster ({commit}, importFile) {
      try {
        const recruitmentMediaIf = new recruitmentMediaInterface()
        const payload = recruitmentMediaIf.makeItemMasterDataImportPayload(importFile)
        return await recruitmentMediaApi.uploadRecruitmentMediaFile(payload)
          .then((response) => {
            if (response.data.error_message) {
              throw response.data
            }
            // インポートした媒体連携項目マスターをストアに追加
            if (response.data) {
              commit('importMediaCooperationItemMaster', response.data)
            }
            return response.data
          })
          .catch(error => {
            throw error
          })
      } catch (error) {
        throw error
      }
    },
    /**
     * 媒体連携項目マスターエクスポート
     * @param {Object} param0 ストアオブジェクト
     * @param {Object} queryParameter エクスポートクエリパラメーター
     */
    exportMediaCooperationItemMaster ({commit}, masterId) {
      try {
        const recruitmentMediaIf = new recruitmentMediaInterface()
        const queryParameter = recruitmentMediaIf.makeItemMasterDataExportParameter(masterId)
        return recruitmentMediaApi.getRecruitmentMediaFile(queryParameter)
          .then((res) => {
            // レスポンスをダウンロード
            fileDownload.fileDownload(res)
            return
          })
          .catch(error => {
            throw error
          })
      } catch (error) {
        throw error
      }
    },
    /**
     * 媒体連携項目マスター行追加
     * @param {Object} param0 ストアオブジェクト
     * @param {Number} master_id 媒体連携項目マスターID
     * @param {Number} row_no 追加行番号
     * @param {Boolean} isNew 新規作成フラグ
     * @returns promise
     */
    async addItemRow ({commit}, {masterId, rowNo, isNew}) {
      try {
        if (isNew === false) {
          const recruitmentMediaIf = new recruitmentMediaInterface()
          const [result, payload] = recruitmentMediaIf.addItemRowPayload(masterId, rowNo)
          if (result === false) {
            throw [result, payload]
          }
          return await recruitmentMediaApi.patchMediaCooperationItemMaster(payload)
            .then (response => {
              commit('addItemRow', {addData: response.data, rowNo: rowNo, isNew: isNew})
              commit('dataRenumbering')
            })
            .catch(error => {
              throw error
            })
        } else {
          commit('addItemRow', {addData: '', rowNo: rowNo, isNew: isNew})
          commit('dataRenumbering')
        }
      } catch (error) {
        throw error
      }
    },
    /**
     * 媒体連携項目マスター行削除
     * @param {Object} param0 ストアオブジェクト
     * @param {Number} master_id 媒体連携項目マスターID
     * @param {Number} row_no 削除行番号
     * @param {Array} delRowIdList 削除行IDリスト
     * @param {Boolean} isNew 新規作成フラグ
     */
    delItemRow ({commit}, {masterId, rowNo, delRowIdList, isNew: isNew}) {
      try {
        if (isNew === false) {
          const recruitmentMediaIf = new recruitmentMediaInterface()
          const [result, payload] = recruitmentMediaIf.delItemRowPayload(masterId, rowNo, delRowIdList)
          if (result === false) {
            throw [result, payload]
          }
          recruitmentMediaApi.patchMediaCooperationItemMaster(payload)
            .then (() => {
              commit('delItemRow', rowNo)
              commit('dataRenumbering')
            })
            .catch(error => {
              throw error
            })
        } else {
          commit('delItemRow', rowNo)
          commit('dataRenumbering')
        }
      } catch (error) {
        throw error
      }
    },
    /**
     * 媒体連携項目マスター編集キャンセル
     * @param {Object} param0 ストアオブジェクト
     * @param {Number} masterId 媒体連携項目マスターID
     */
    editCancel ({commit}, masterId) {
      try {
        const recruitmentMediaIf = new recruitmentMediaInterface()
        const [result, payload] = recruitmentMediaIf.editCancelPayload(masterId)
        if (result === false) {
          throw [result, payload]
        }
        recruitmentMediaApi.patchMediaCooperationItemMaster(payload)
          .catch(error => {
            throw error
          })
      } catch (error) {
        throw error
      }
    },
    /**
     * 媒体連携項目マスター編集後処理
     * @param {Object} param0 ストアオブジェクト
     * @param {Number} masterId 媒体連携項目マスターID
     */
    editTerminate ({commit}, masterId) {
      try {
        const recruitmentMediaIf = new recruitmentMediaInterface()
        const [result, payload] = recruitmentMediaIf.editTerminatePayload(masterId)
        if (result === false) {
          throw [result, payload]
        }
        recruitmentMediaApi.patchMediaCooperationItemMaster(payload)
          .catch(error => {
            throw error
          })
      } catch (error) {
        throw error
      }
    },
  },
  mutations: {
    /**
     * モーダルオープン
     * @param {Object} state ステート
     * @param {String} modalName モーダル名
     */
    openModal (state, modalName) {
      state.modalState[modalName] = true
    },
    /**
     * モーダルクローズ
     * @param {Object} state ステート
     * @param {String} modalName モーダル名
     */
    closeModal (state, modalName) {
      state.modalState[modalName] = false
    },

    // ==================================================
    // 媒体情報関連
    // ==================================================

    /**
     * 媒体情報リストセット
     * @param {Object} state ステート
     * @param {Object} recruitmentMediaInfoList 媒体情報リスト
     */
    setRecruitmentMediaInfo (state, recruitmentMediaInfoList) {
      // 媒体情報リストをステートにセットする
      state.recruitmentMediaInfoList = Object.assign(
        state.recruitmentMediaInfoList,
        recruitmentMediaInfoList
      )
    },
    /**
     * 選考状況リストセット
     * @param {Object} state ステート
     * @param {Object} progressList 選考状況リスト
     */
    setSeekerFieldList (state, progressList) {
      // 選考状況リストをステートにセットする
      state.progressList = Object.assign(state.progressList, progressList)
    },
    /**
     * Seekerフィールドリストセット
     * @param {Object} state ステート
     * @param {Object} seekerFieldList Seekerフィールドリスト
     */
    setSeekerFieldList (state, seekerFieldList) {
      // Seekerフィールドリストをステートにセットする
      state.seekerFieldList = Object.assign(state.seekerFieldList, seekerFieldList)
    },

    // ==================================================
    // 媒体連携項目マスター関連
    // ==================================================

    /**
     * 媒体連携項目マスター設定
     * @param {Object} state ステート
     * @param {Object} currentMaster 媒体連携項目マスター
     */
    setCurrentMaster (state, currentMaster) {
      state.currentMaster = Object.assign(state.currentMaster, JSON.parse(JSON.stringify(currentMaster)))
    },
    /**
     * 表示中媒体連携項目マスタークリア
     * @param {Object} state ステート
     */
    clearCurrentMaster (state) {
      state.currentMaster = Object.assign(
        state.currentMaster,
        JSON.parse(JSON.stringify(state.mediaCooperationItemMasterTemplate))
      )
    },
    /**
     * 媒体連携項目マスター一覧セット
     * @param {Object} state ステート
     * @param {Object} mediaCooperationItemMasterNameList 媒体連携項目マスター一覧
     */
    setItemMasterList (state, mediaCooperationItemMasterNameList) {
      // 媒体連携項目マスター一覧をステートにセットする
      state.mediaCooperationItemMasterNameList = Object.assign(
        state.mediaCooperationItemMasterNameList,
        mediaCooperationItemMasterNameList
      )
    },
    /**
     * 媒体連携項目マスターデータセット
     * @param {Object} state ステート
     * @param {Object} mediaCooperationItemMasterData 媒体連携項目マスターデータ
     */
    setMediaCooperationItemMaster (state, mediaCooperationItemMasterData) {
      // 媒体連携項目マスターリストをステートにセットする
      if (state.currentMaster.common_item_data.data[0].id) {
        // 分割取得時はデータを追加
        state.currentMaster.common_item_data.data =
          state.currentMaster.common_item_data.data.concat(
            mediaCooperationItemMasterData.common_item_data.data
          )
        for (const media_index in mediaCooperationItemMasterData.media_item_data) {
          state.currentMaster.media_item_data[media_index].data =
            state.currentMaster.media_item_data[media_index].data.concat(
              mediaCooperationItemMasterData.media_item_data[media_index].data
            )
        }
      } else {
        // 1回または初回取得時はそのまま格納する
        state.currentMaster = Object.assign(state.currentMaster, mediaCooperationItemMasterData)
      }
      // 最大行数
      let dataMaxRow = 0
      dataMaxRow = state.currentMaster.common_item_data.data.length
      for (const media_index in state.currentMaster.media_item_data) {
        if (dataMaxRow > state.currentMaster.media_item_data[media_index].data.length) {
          dataMaxRow = state.currentMaster.media_item_data[media_index].data.length
        }
      }
      state.loadCount = dataMaxRow
    },
    /**
     * 媒体連携項目マスターテンプレートセット
     * @param {Object} state ステート
     * @param {Object} mediaCooperationItemMasterTemplate 媒体連携項目マスターテンプレート
     */
    setItemMasterTemplate (state, mediaCooperationItemMasterTemplate) {
      // 媒体連携項目マスターリストをステートにセットする (参照元が書き換わらないようにディープコピーする)
      state.mediaCooperationItemMasterTemplate = Object.assign(
        state.mediaCooperationItemMasterTemplate,
        JSON.parse(JSON.stringify(mediaCooperationItemMasterTemplate))
      )
      // 項目データのテンプレートを別で保存しておく (参照元が書き換わらないようにディープコピーする)
      state.itemDataTemplate = Object.assign(
        state.itemDataTemplate,
        JSON.parse(JSON.stringify(mediaCooperationItemMasterTemplate.common_item_data.data[0]))
      )
      // 選択中マスターにも格納しておく (参照元が書き換わらないようにディープコピーする)
      state.currentMaster = Object.assign(
        state.currentMaster,
        JSON.parse(JSON.stringify(mediaCooperationItemMasterTemplate))
      )
    },
    /**
     * 媒体連携項目マスターデータ追加
     * @param {Object} state ステート
     * @param {Object} mediaCooperationItemMaster 媒体連携項目マスターデータ
     */
    createMediaCooperationItemMaster (state, mediaCooperationItemMaster) {
      // master_idが空のステートの媒体連携項目マスターリストを更新する
      for (let index in state.mediaCooperationItemMasterNameList) {
        if (!state.mediaCooperationItemMasterNameList[index].id) {
          state.mediaCooperationItemMasterNameList.splice(index, 1, mediaCooperationItemMaster)
        }
        state.mediaCooperationItemMasterNameList[index].order = index
      }
      state.currentMaster.master_id = mediaCooperationItemMaster.id
    },
    /**
     * 媒体連携項目マスターデータインポート
     * @param {Object} state ステート
     * @param {Object} mediaCooperationItemMaster 媒体連携項目マスターデータ
     */
    importMediaCooperationItemMaster (state, mediaCooperationItemMaster) {
      // 媒体連携項目マスターリストに追加する
      for (let index in state.mediaCooperationItemMasterNameList) {
        // 表示順を付け直しておく
        state.mediaCooperationItemMasterNameList[index].order = index
        // 新しい項目マスターの一覧への追加
        if (!state.mediaCooperationItemMasterNameList[index].id) {
          // 追加しなおし用に削除する前に退避
          let master = JSON.parse(JSON.stringify(state.mediaCooperationItemMasterNameList[index]))
          // 一度リストから削除する
          state.mediaCooperationItemMasterNameList.splice(index, 1)
          master.order = index
          // 新しい表示順で追加する
          state.mediaCooperationItemMasterNameList.splice(mediaCooperationItemMaster.order, 0, master)
          state.loadCount = mediaCooperationItemMaster.data_row_count
        }
      }
      // 表示中マスターにする
      state.currentMaster = Object.assign(state.currentMaster, mediaCooperationItemMaster)
    },
    /**
     * 媒体連携項目マスターデータ更新
     * @param {Object} state ステート
     * @param {Object} mediaCooperationItemMaster 媒体連携項目マスターデータ
     */
    updateMediaCooperationItemMaster (state, mediaCooperationItemMaster) {
      // ステートの媒体連携項目マスターを更新する
      state.currentMaster = Object.assign(state.currentMaster, mediaCooperationItemMaster)
      state.loadCount = state.currentMaster.data_row_count
    },
    /**
     * 媒体連携項目マスターリスト表示順付け直し
     * @param {Object} state ステート
     * @param {Object} itemMaster 媒体連携項目マスターデータ
     */
    updateMediaCooperationItemMasterSort (state, itemMaster) {
      for (let index in state.mediaCooperationItemMasterNameList) {
        if (itemMaster.master_id === state.mediaCooperationItemMasterNameList[index].id) {
          // 追加しなおし用に削除する前に退避
          let master = JSON.parse(JSON.stringify(state.mediaCooperationItemMasterNameList[index]))
          // 一度リストから削除する
          state.mediaCooperationItemMasterNameList.splice(index, 1)
          master.master_name = itemMaster.master_name
          master.order = itemMaster.order
          // 新しい表示順で追加する
          state.mediaCooperationItemMasterNameList.splice(itemMaster.order, 0, master)
        }
      }
      // 表示順を新たに付け直す
      state.mediaCooperationItemMasterNameList.forEach((master, index) => {
        master.order = index
      })
    },
    /**
     * 媒体連携項目マスター名更新
     * @param {Object} state ステート
     * @param {Object} masterList 媒体連携項目マスターリスト
     */
    updateMediaCooperationItemMasterName (state, masterList) {
      // ステートの媒体連携項目マスター名を更新する
      for (let index in masterList) {
        if (masterList[index].id === state.currentMaster.master_id) {
          state.currentMaster.master_id = masterList[index].id
          state.currentMaster.master_name = masterList[index].master_name
          state.currentMaster.is_working = masterList[index].is_working
          state.currentMaster.order = masterList[index].order
          state.currentMaster.data_row_count = masterList[index].data_row_count
        }
      }
      state.mediaCooperationItemMasterNameList = Object.assign(
        state.mediaCooperationItemMasterNameList,
        masterList
      )
    },
    /**
     * 媒体連携項目マスターデータ削除
     * @param {Object} state ステート
     * @param {Object} mediaCooperationItemMaster 媒体連携項目マスターデータ
     */
    deleteMediaCooperationItemMaster (state, masterId) {
      // ステートの媒体連携項目マスターリストを削除する
      for (let index in state.mediaCooperationItemMasterNameList) {
        if (state.mediaCooperationItemMasterNameList[index].id === masterId) {
          state.mediaCooperationItemMasterNameList.splice(index, 1)
          break
        }
      }
      if (state.mediaCooperationItemMasterNameList.length === 0) {
        state.mediaCooperationItemMasterNameList = Object.assign(
          state.mediaCooperationItemMasterNameList,
          []
        )
      }
      // 選択中マスターを空にしておく
      state.currentMaster = Object.assign(
        state.currentMaster,
        JSON.parse(JSON.stringify(state.mediaCooperationItemMasterTemplate))
      )
    },
    /**
     * 媒体連携項目マスター行追加
     * @param {Object} state ステート
     * @param {Object} addData 追加行データ
     * @param {Number} row_no 追加行番号
     * @param {Boolean} isNew 新規作成フラグ
     */
    addItemRow (state, {addData, rowNo, isNew}) {
      if (isNew === false) {
        state.currentMaster.common_item_data.data.splice(
          rowNo,
          0,
          addData[state.currentMaster.common_item_data.header.media_list_id]
        )
        state.currentMaster.media_item_data.forEach(mediaData => {
          if (addData[mediaData.header.media_list_id]) {
            mediaData.data.splice(rowNo, 0, addData[mediaData.header.media_list_id])
          } else {
            mediaData.data.splice(rowNo, 0, state.itemDataTemplate)
          }
        })
      } else {
        // 共通項目に行を追加
        state.currentMaster.common_item_data.data.splice(
          rowNo,
          0,
          JSON.parse(JSON.stringify(state.itemDataTemplate))
        )
        //各媒体項目に行を追加
        state.currentMaster.media_item_data.forEach(mediaData => {
          mediaData.data.splice(rowNo, 0, JSON.parse(JSON.stringify(state.itemDataTemplate)))
        })
      }
      state.currentMaster.data_row_count = state.currentMaster.common_item_data.data.length
      state.loadCount = state.currentMaster.data_row_count
    },
    /**
     * 媒体連携項目マスター行削除
     * @param {Object} state ステート
     * @param {Number} rowNo 削除行番号
     */
    delItemRow (state, rowNo) {
      // 共通項目データの行を削除
      state.currentMaster.common_item_data.data.splice(rowNo, 1)
      // 媒体項目データの行を削除
      state.currentMaster.media_item_data.forEach(mediaData => {
        mediaData.data.splice(rowNo, 1)
      })
      state.currentMaster.data_row_count = state.currentMaster.common_item_data.data.length
      state.loadCount = state.currentMaster.data_row_count
      if (state.currentMaster.common_item_data.data.length === 0) {
        // 最後の1行を削除した場合は、空行と入れ替える
        state.currentMaster.common_item_data.data.splice(
          rowNo,
          1,
          JSON.parse(JSON.stringify(state.itemDataTemplate))
        )
        state.currentMaster.media_item_data.forEach(mediaData => {
          mediaData.data.splice(
            rowNo,
            1,
            JSON.parse(JSON.stringify(state.itemDataTemplate))
          )
        })
        state.currentMaster.data_row_count = 1
        state.loadCount = state.currentMaster.data_row_count
      }
    },
    /**
     * 媒体連携項目マスター行番号振り直し
     * @param {Object} state ステート
     */
    dataRenumbering (state) {
      // 共通項目の行番号を振り直す
      state.currentMaster.common_item_data.data.forEach((itemData, index) => {
        itemData.row_no = index
        // リアクタブルに反映させるため、spliceで入れ替える
        state.currentMaster.common_item_data.data.splice(index, 1, itemData)
      })
      // 媒体側項目の行番号を振り直す
      state.currentMaster.media_item_data.forEach(mediaData => {
        mediaData.data.forEach((itemData, index) => {
          itemData.row_no = index
          // リアクタブルに反映させるため、spliceで入れ替える
          mediaData.data.splice(index, 1, itemData)
        })
      })
    },
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/recruitment-media/recruitment-media-store.js